import axiosInstance from './axiosCreator';
import publicApi from './publicApi';

export interface WebinarPollQuestion {
  key: string;
  prompt: string;
  options: string[];
}

export type WebinarLiveGame = 'SURVIVAL' | 'POKEMON_OR_DRUG';

export type WebinarLivePhase =
  | { type: 'WELCOME'; params: { questions: WebinarPollQuestion[] } }
  | { type: 'VOUCHER_POLL'; params: { questions: WebinarPollQuestion[] } }
  | { type: 'VOUCHER_CLAIM'; params: Record<string, never> }
  | { type: 'POLL'; params: { questions: WebinarPollQuestion[] } }
  | {
      type: 'QBANK_QUESTION';
      params: { vignetteId: string; variantId: string };
    }
  | {
      type: 'FREE_EXPLORE';
      params: { key: 'qbank-tour' | 'app-tour' };
    }
  | { type: 'FLASHCARDS'; params: { flashcardIds: string[] } }
  | { type: 'VIDEO'; params: { variantId: string } }
  | { type: 'GAME'; params: { game: WebinarLiveGame } }
  | { type: 'THANKS'; params: Record<string, never> }
  | { type: 'ONE_PAGER'; params: Record<string, never> };

export interface WebinarLiveSession {
  id: string;
  status: 'LOBBY' | 'LIVE' | 'ENDED';
  currentPhaseIndex: number | null;
  advanceNonce: number;
  phases: WebinarLivePhase[];
  startedAt: string | null;
}

export interface WebinarPollAggregate {
  pollKey: string;
  totalResponses: number;
  optionCounts: Record<string, number>;
}

export interface WebinarLiveSchool {
  id: string;
  name: string;
  shortName: string | null;
  image: string | null;
}

export interface WebinarLiveState {
  // The success contract carries a school. Keep null defensive handling for a
  // stale/invalid cohort payload so the public route can render its 404 state.
  school: WebinarLiveSchool | null;
  session: WebinarLiveSession | null;
  serverNow: string;
  attendeeCount: number | null;
  pollAggregates: WebinarPollAggregate[] | null;
}

export interface WebinarAdminState extends WebinarLiveState {
  survivalRoundStatus: 'IDLE' | 'OPEN' | 'FINALE' | null;
  pokemonRoundStatus: 'IDLE' | 'OPEN' | 'FINALE' | null;
  selectedGame: WebinarLiveGame | null;
  counters: {
    attendees: { total: number; byPhase: Record<number, number> } | null;
    signupsSinceStart: number;
    voucherClaimsSinceStart: number;
    questionStats: {
      variantId: string;
      answered: number;
      correct: number;
      percentCorrect: number | null;
    }[];
    flashcardResponsesSinceStart: number;
    pollAggregates: WebinarPollAggregate[];
  };
}

export interface WebinarVideoClip {
  clipId: string;
  variantId: string;
  videoId: string;
  startSeconds: number;
  endSeconds: number;
  reason: string;
  video: {
    id: string;
    title: string;
    thumbnailUrl: string;
    mp4Url: string;
    webmUrl: string | null;
  };
  viewerVote: 'UP' | 'DOWN' | null;
  upvotes: number;
  downvotes: number;
}

export interface WebinarVideoClipState {
  state: 'computing' | 'ready';
  clip: WebinarVideoClip | null;
  videoVersionId: string | null;
}

const base = '/api/v5/webinar-live';
const encoded = (value: string) => encodeURIComponent(value);
const webinarLiveVisitKey = 'oraWebinarLiveVisit';
const webinarLiveVisitTtlMs = 12 * 60 * 60 * 1000;
const webinarRegistrationEmailKey = 'oraWebinarRegistrationEmail';
const webinarRegistrationEmailTtlMs = 30 * 24 * 60 * 60 * 1000;

interface StoredWebinarLiveVisit {
  cohort: string;
  expiresAt: number;
}

interface StoredWebinarRegistrationEmail {
  email: string;
  cohort: string;
  expiresAt: number;
}

export const clearWebinarLiveVisit = () => {
  try {
    localStorage.removeItem(webinarLiveVisitKey);
  } catch {
    // Navigation still works when storage is unavailable.
  }
};

export const rememberWebinarLiveVisit = (cohort: string) => {
  if (!cohort) return;

  try {
    const visit: StoredWebinarLiveVisit = {
      cohort,
      expiresAt: Date.now() + webinarLiveVisitTtlMs,
    };

    localStorage.setItem(webinarLiveVisitKey, JSON.stringify(visit));
  } catch {
    // The live page remains usable when storage is unavailable.
  }
};

export const getActiveWebinarLiveVisit = (): { cohort: string } | null => {
  try {
    const raw = localStorage.getItem(webinarLiveVisitKey);
    if (!raw) return null;

    const value: unknown = JSON.parse(raw);
    if (
      typeof value !== 'object' ||
      value === null ||
      !('cohort' in value) ||
      typeof value.cohort !== 'string' ||
      !value.cohort ||
      !('expiresAt' in value) ||
      typeof value.expiresAt !== 'number' ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= Date.now()
    ) {
      clearWebinarLiveVisit();
      return null;
    }

    return { cohort: value.cohort };
  } catch {
    clearWebinarLiveVisit();
    return null;
  }
};

export const clearWebinarRegistrationEmail = () => {
  try {
    localStorage.removeItem(webinarRegistrationEmailKey);
  } catch {
    // Registration still works when storage is unavailable.
  }
};

export const rememberWebinarRegistrationEmail = (
  email: string,
  cohort: string
) => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) return;

  try {
    const registration: StoredWebinarRegistrationEmail = {
      email: trimmedEmail,
      cohort,
      expiresAt: Date.now() + webinarRegistrationEmailTtlMs,
    };

    localStorage.setItem(
      webinarRegistrationEmailKey,
      JSON.stringify(registration)
    );
  } catch {
    // Registration still works when storage is unavailable.
  }
};

export const getWebinarRegistrationEmail = (): string | null => {
  try {
    const raw = localStorage.getItem(webinarRegistrationEmailKey);
    if (!raw) return null;

    const value: unknown = JSON.parse(raw);
    if (
      typeof value !== 'object' ||
      value === null ||
      !('email' in value) ||
      typeof value.email !== 'string' ||
      !value.email ||
      !value.email.trim() ||
      !('cohort' in value) ||
      typeof value.cohort !== 'string' ||
      !('expiresAt' in value) ||
      typeof value.expiresAt !== 'number' ||
      !Number.isFinite(value.expiresAt) ||
      value.expiresAt <= Date.now()
    ) {
      clearWebinarRegistrationEmail();
      return null;
    }

    return value.email.trim();
  } catch {
    // The email stays optional when storage is unavailable or malformed.
    clearWebinarRegistrationEmail();
    return null;
  }
};

const unwrap = <T>(response: {
  data: { success: boolean; data?: T; error?: string };
}) => {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || 'Webinar request failed');
  }

  return response.data.data;
};

export const fetchWebinarLiveState = async (cohort: string) =>
  unwrap<WebinarLiveState>(
    await publicApi.get(`${base}/${encoded(cohort)}/state`, { timeout: 10_000 })
  );

/**
 * Presence is best-effort, so it deliberately avoids `axiosInstance`: its
 * interceptor signs the tab out on a 401, and a background heartbeat must
 * never sign a student out mid-webinar. The session cookie still rides along
 * (`publicApi` sends credentials).
 */
export const sendWebinarHeartbeat = (
  cohort: string,
  sessionId: string,
  phaseIndex: number
) => {
  return publicApi.post(`${base}/${encoded(cohort)}/heartbeat`, {
    sessionId,
    phaseIndex,
  });
};
/**
 * Reopens the caller's DONE survival run so they can retry during a live
 * webinar GAME phase (the /play event contract is otherwise one attempt per
 * guest). Best score is preserved server-side.
 */
export const retryWebinarSurvival = (cohort: string, guestId: string) =>
  axiosInstance.post(`${base}/${encoded(cohort)}/survival/retry`, { guestId });

export const retryWebinarPokemon = (cohort: string, guestId: string) =>
  axiosInstance.post(`${base}/${encoded(cohort)}/pokemon/retry`, { guestId });

export const submitWebinarPollResponse = (
  cohort: string,
  sessionId: string,
  pollKey: string,
  answer: string
) =>
  axiosInstance.post(`${base}/${encoded(cohort)}/poll-response`, {
    sessionId,
    pollKey,
    answer,
  });
export const createWebinarQbankBlock = async (
  cohort: string,
  variantId: string
) =>
  unwrap<unknown>(
    await axiosInstance.post(`${base}/${encoded(cohort)}/qbank-block`, {
      variantId,
    })
  );
export const fetchWebinarVideoClip = async (
  cohort: string,
  variantId: string
): Promise<WebinarVideoClipState | null> => {
  try {
    return unwrap<WebinarVideoClipState>(
      await axiosInstance.get(
        `${base}/${encoded(cohort)}/video-clip/${encoded(variantId)}`
      )
    );
  } catch {
    return null;
  }
};
export const createWebinarSession = async (cohort: string) =>
  unwrap<WebinarLiveSession>(
    await axiosInstance.post(`${base}/admin/sessions`, { cohort })
  );
export const startWebinarSession = (sessionId: string) =>
  axiosInstance.post(`${base}/admin/sessions/${encoded(sessionId)}/start`);
/**
 * `fromIndex` makes the advance a compare-and-swap: when the room has already
 * moved on (a second console tab, a stale render) the backend answers 409
 * instead of dragging everyone back.
 */
export const advanceWebinarSession = (
  sessionId: string,
  toIndex: number,
  fromIndex?: number | null
) =>
  axiosInstance.post(`${base}/admin/sessions/${encoded(sessionId)}/advance`, {
    toIndex,
    fromIndex,
  });
export const endWebinarSession = (sessionId: string) =>
  axiosInstance.post(`${base}/admin/sessions/${encoded(sessionId)}/end`);
/** Revives an ENDED session in place so the room resumes without reloading. */
export const reopenWebinarSession = (sessionId: string) =>
  axiosInstance.post(`${base}/admin/sessions/${encoded(sessionId)}/reopen`);
export const resetWebinarSession = (sessionId: string) =>
  axiosInstance.post(`${base}/admin/sessions/${encoded(sessionId)}/reset`);
export const setWebinarGameRound = (
  sessionId: string,
  status: 'OPEN' | 'FINALE'
) =>
  axiosInstance.post(
    `${base}/admin/sessions/${encoded(sessionId)}/game-round`,
    { status }
  );
export const setWebinarGameSelection = (
  sessionId: string,
  game: WebinarLiveGame
) =>
  axiosInstance.post(`${base}/admin/sessions/${encoded(sessionId)}/game`, {
    game,
  });
export const fetchWebinarAdminState = async (cohort: string) =>
  unwrap<WebinarAdminState>(
    await axiosInstance.get(`${base}/admin/state`, { params: { cohort } })
  );
